import { useState } from 'react';
import { Alert } from "antd";
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  FormField,
  Input,
} from '@shared/ui';
import { ApiError } from '@shared/api';
import { applyApiFieldErrors } from '@shared/lib/forms/apply-field-errors';
import {
  changePasswordSchema,
  useChangePasswordMutation,
  type ChangePasswordValues,
} from '@entities/account';

export function ChangePasswordCard() {
  const { t } = useTranslation();
  const mutation = useChangePasswordMutation();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const form = useForm<ChangePasswordValues>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  const onSubmit = (values: ChangePasswordValues) => {
    setSubmitError(null);
    setSuccess(false);
    mutation.mutate(
      { currentPassword: values.currentPassword, newPassword: values.newPassword },
      {
        onSuccess: () => {
          setSuccess(true);
          form.reset();
        },
        onError: (err) => {
          if (err instanceof ApiError) {
            if (err.status === 401) {
              form.setError('currentPassword', {
                type: 'server',
                message: t('settings.changePassword.wrongCurrent'),
              });
              return;
            }
            if (err.code === 'DOMAIN_RULE_VIOLATION') {
              form.setError('newPassword', { type: 'server', message: err.message });
              return;
            }
          }
          if (!applyApiFieldErrors(err, form.setError)) {
            setSubmitError(err instanceof ApiError ? err.message : 'Password change failed');
          }
        },
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('settings.changePassword.title')}</CardTitle>
        <CardDescription>{t('settings.changePassword.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4 max-w-md" noValidate>
            <FormField
              label={t('settings.changePassword.current')}
              required
              error={form.formState.errors.currentPassword?.message}
            >
              {(p) => (
                <Input
                  {...p}
                  type="password"
                  autoComplete="current-password"
                  {...form.register('currentPassword')}
                />
              )}
            </FormField>
            <FormField
              label={t('settings.changePassword.new')}
              required
              error={form.formState.errors.newPassword?.message}
            >
              {(p) => (
                <Input
                  {...p}
                  type="password"
                  autoComplete="new-password"
                  {...form.register('newPassword')}
                />
              )}
            </FormField>
            <FormField
              label={t('settings.changePassword.confirm')}
              required
              error={form.formState.errors.confirmPassword?.message}
            >
              {(p) => (
                <Input
                  {...p}
                  type="password"
                  autoComplete="new-password"
                  {...form.register('confirmPassword')}
                />
              )}
            </FormField>

            {submitError ? <Alert type="error" showIcon message={submitError} /> : null}
            {success ? <Alert type="success" showIcon message={t('settings.changePassword.success')} /> : null}

            <Button type="submit" loading={mutation.isPending}>
              {t('settings.changePassword.submit')}
            </Button>
          </form>
        </FormProvider>
      </CardContent>
    </Card>
  );
}
